import {FlexLayoutConfig} from './flex-layout.config';
import {FieldData} from './flex-layout/flex-layout.component';
import {FlexLayoutItem} from './flex-layout-item';
import {LayoutEnum} from './layout.enum';
import {Injectable} from '@angular/core';

@Injectable()
export class FlexLayoutService {

    private fieldsMatrix: string[][];

    private layoutGrid: FlexLayoutItem[];
    private layoutConfig: FlexLayoutConfig;

    private currentField: FieldData;
    private currentLayout: FlexLayoutItem;
    private nextField: FieldData;
    private fieldElementMap = new Map<string, FlexLayoutItem>();
    private styleElement;

    /**
     * Creates matrix of elements using layout config.
     *
     * @param layoutConfig - layoutConfig
     */
    public createFieldsMatrix(layoutConfig: FlexLayoutConfig) {
        this.fieldsMatrix = [];

        layoutConfig.fields.forEach((field) => {
            const rowspan = field.rowspan || 1;
            const colspan = field.colspan || 1;
            for (let i = field.row; i < field.row + rowspan; i++) {
                for (let j = field.col; j < field.col + colspan; j++) {
                    if (!this.fieldsMatrix[i]) {
                        this.fieldsMatrix[i] = [];
                    }

                    if (this.fieldsMatrix[i][j]) {
                        console.error(`Element '${this.fieldsMatrix[i][j]}' intersects with '${field.fieldName}'`);
                    }

                    this.fieldsMatrix[i][j] = field.fieldName;
                }
            }
        });
    }

    /**
     * Returns common field data for creating a layout.
     *
     * @param fieldName - fieldName
     * @param layoutConfig - layoutConfig
     */
    public getFieldData(fieldName: string, layoutConfig: FlexLayoutConfig): FieldData {
        let fieldConfig;
        for (const field of layoutConfig.fields) {
            if (field.fieldName === fieldName) {
                fieldConfig = field;
            }
        }
        if (!fieldConfig) {
            console.error(`Can't find field config for '${fieldName}'.`);
        }

        const rowspan = fieldConfig.rowspan || 1;
        const colspan = fieldConfig.colspan || 1;

        return {config: fieldConfig, row: fieldConfig.row, col: fieldConfig.col, rowspan, colspan};
    }

    /**
     * @param layoutConfig - layoutConfig
     */
    public createLayoutGrid(layoutConfig: FlexLayoutConfig): FlexLayoutItem[] {
        this.createFieldsMatrix(layoutConfig);

        this.currentField = this.getFieldData(this.fieldsMatrix[0][0], layoutConfig);
        this.currentLayout = new FlexLayoutItem({
            layout: LayoutEnum.row,
            col: 0,
            row: 0,
            items: []
        });

        this.layoutGrid = [this.currentLayout];
        this.layoutConfig = layoutConfig;

        this.fieldElementMap.clear();

        while (this.fieldElementMap.size < layoutConfig.fields.length) {
            if (this.fieldElementMap.get(this.currentField.config.fieldName)) {
                console.error(`Field with name ${this.currentField.config.fieldName} has been already created.`);
            }

            const fieldElement = this.createFieldElement();
            this.currentLayout.items.push(fieldElement);
            this.fieldElementMap.set(this.currentField.config.fieldName, fieldElement);

            if (this.fieldElementMap.size >= layoutConfig.fields.length) {
                break;
            }

            this.recalculateLayoutsDimension(fieldElement);

            if (this.isElementTheLastInMatrixRow()) {
                continue;
            }

            if (this.isElementTheLastInColumnRow()) {
                continue;
            }

            const fieldName = this.fieldsMatrix[this.currentField.row][this.currentField.col + this.currentField.colspan];
            this.nextField = this.getFieldData(fieldName, layoutConfig);

            if (this.isNextFieldRowspanEquelThenCurrentLayout()) {
                continue;
            }

            if (this.isNextFieldRowspanGreaterThenCurrentLayout()) {
                continue;
            }

            this.isNextFieldRowspanLessThenCurrentLayout();
        }

        this.setFieldsLayoutsSize();
        this.setRowClass(this.layoutGrid);
        this.setColClass(this.layoutGrid);
        return this.layoutGrid;
    }

    private setColClass(layoutGridItem: FlexLayoutItem[]) {
        layoutGridItem.forEach((item: FlexLayoutItem, index: number) => {
            if (item?.items) {
                const firstColClass = 'col-first';
                const colClass = 'mydocma-form-layout-col';
                const lastColClass = 'col-last';
                item?.items.forEach((layoutItem, i) => {
                    const classSet = new Set();
                    classSet.add(colClass);
                    if (layoutItem?.class) {
                        classSet.add(layoutItem?.class);
                    }
                    if (i === 0) {
                        classSet.add(firstColClass);
                    }
                    if (item?.items.length - 1 === i) {
                        classSet.add(lastColClass);
                    }
                    layoutItem.class = Array.from(classSet).join(' ');
                })
            }
        })
    }

    private setRowClass(layoutGridItem: FlexLayoutItem[]): void {
        if (!this.layoutConfig?.rows?.length) {
            return
        }

        layoutGridItem.forEach((layoutGridSubItem, index) => {

            const ind = Object.values(this.layoutConfig.rows).findIndex(row => row.row === index);
            if (ind > -1) {
                if (layoutGridSubItem?.class?.length) {
                    const itemClass = new Set(layoutGridSubItem.class.split(' '));
                    this.layoutConfig.rows[ind].class.split(' ')
                        .forEach(rowClass => itemClass.add(rowClass));
                    layoutGridSubItem.class = Array.from(itemClass).join(' ');

                } else {
                    layoutGridSubItem.class = this.layoutConfig.rows[ind].class;
                }

            }
        })

    }

    private createFieldElement() {
        let maxCollapsedWidth = this.currentField.config?.maxCollapsedWidth || null;
        if (this.currentField.config?.maxCollapsedWidth === 0) {
            maxCollapsedWidth = 0;
        }

        let cssClass = '';
        if (this.currentField.config.width || this.currentField.config.minWidth || this.currentField.config.maxWidth) {
            cssClass = `${this.currentField.config.fieldName}`;
            this.createCssClass(
                cssClass,
                this.currentField.config.width,
                this.currentField.config.minWidth,
                this.currentField.config.maxWidth
            );
        }
        if (this.currentField.config.class) {
            cssClass = `${cssClass} ${this.currentField.config.class}`;
        }

        return new FlexLayoutItem({
            layout: LayoutEnum.row,
            layoutAlign: this.currentField.config.layoutAlign,
            flex: this.currentField.config.flex,
            class: cssClass,
            name: this.currentField.config.fieldName,
            maxCollapsedWidth,
            parentLayout: this.currentLayout,
            col: this.currentField.col,
            row: this.currentField.row,
            rowspan: this.currentField.rowspan,
            colspan: this.currentField.colspan
        });
    }

    private setFieldsLayoutsSize() {
        this.fieldElementMap.forEach((flexLayoutItem, fieldName) => {
            this.currentField = this.getFieldData(fieldName, this.layoutConfig);

            if (this.currentField.config.flex) {
                return;
            }


            let fxFlex = 0;
            for (const colConfig of this.layoutConfig.cols) {
                if (this.currentField.col <= colConfig.col
                    && colConfig.col < this.currentField.col + this.currentField.colspan
                ) {
                    fxFlex += Number(colConfig.flex);
                }
            }

            if (fxFlex) {
                flexLayoutItem.flex = fxFlex.toString();
            }
        });
    }

    /**
     * Recalculates dimension (rowspan, colspan) of layouts after adding of a new element.
     *
     * @param elementLayout - Element layout that was added.
     */
    private recalculateLayoutsDimension(elementLayout: FlexLayoutItem) {
        this.currentLayout.rowspan = elementLayout.rowspan;
        this.currentLayout.colspan += elementLayout.colspan;

        const parentLayout = this.currentLayout.parentLayout;
        if (parentLayout && parentLayout.layout === LayoutEnum.column) {
            if (parentLayout.col === this.currentField.col) {
                parentLayout.rowspan += this.currentField.rowspan;
            }
        }
    }

    /**
     *
     */
    private isElementTheLastInMatrixRow(): boolean {
        if (this.currentField.col + this.currentField.colspan < this.fieldsMatrix[this.currentField.row].length) {
            return false;
        }

        const parentLayout = this.currentLayout.parentLayout;
        if (!parentLayout || parentLayout.layout === LayoutEnum.row && !parentLayout.parentLayout) {
            this.currentField = this.getFieldData(
                this.fieldsMatrix[this.currentLayout.row + this.currentLayout.rowspan][0],
                this.layoutConfig
            );
            this.currentLayout = new FlexLayoutItem({
                layout: LayoutEnum.row,
                items: [],
                col: this.currentField.col,
                row: this.currentField.row
            });
            this.layoutGrid.push(this.currentLayout);

            return true;
        }
        parentLayout.colspan = this.currentLayout.colspan;
        parentLayout.rowspan += this.currentLayout.rowspan;
        if (parentLayout.layout === LayoutEnum.column) {
            if (parentLayout.parentLayout.rowspan === parentLayout.rowspan) {
                this.currentField = this.getFieldData(
                    this.fieldsMatrix[parentLayout.row + parentLayout.rowspan][0],
                    this.layoutConfig
                );
                this.currentLayout = new FlexLayoutItem({
                    layout: LayoutEnum.row,
                    items: [],
                    col: this.currentField.col,
                    row: this.currentField.row
                });
                this.layoutGrid.push(this.currentLayout);

                return true;
            }

            this.currentField = this.getFieldData(
                this.fieldsMatrix[this.currentLayout.row + this.currentLayout.rowspan][this.currentLayout.col],
                this.layoutConfig
            );
            this.currentLayout = new FlexLayoutItem({
                layout: LayoutEnum.row,
                parentLayout,
                items: [],
                col: this.currentField.col,
                row: this.currentField.row
            });
            parentLayout.items.push(this.currentLayout);

            return true;
        }

        return true;
    }

    /**
     * Column can have fixed size. After reaching it we check if
     * we can create row with this column. Row has higher priority over column.
     */
    private isElementTheLastInColumnRow(): boolean {
        let fieldName;
        const columnLayout = this.currentLayout.parentLayout;
        if (!columnLayout) {
            return false;
        }
        if (columnLayout.layout !== LayoutEnum.column) {
            return false;
        }
        if (columnLayout.colspan > this.currentLayout.colspan) {
            return false;
        }

        fieldName = this.fieldsMatrix[columnLayout.row][columnLayout.col + columnLayout.colspan];
        this.nextField = this.getFieldData(fieldName, this.layoutConfig);

        // if also exists next layout and layout has items
        if (this.nextField.rowspan <= columnLayout.rowspan) {
            this.currentField = this.nextField;

            const parentContainer = columnLayout.parentLayout;
            this.currentLayout = new FlexLayoutItem({
                layout: LayoutEnum.row,
                parentLayout: parentContainer,
                col: this.currentField.col,
                row: this.currentField.row,
                items: []
            });
            parentContainer.items.push(this.currentLayout);

            return true;
        }

        fieldName = this.fieldsMatrix[this.currentLayout.row + this.currentLayout.rowspan][columnLayout.col];
        this.currentField = this.getFieldData(fieldName, this.layoutConfig);

        this.currentLayout = new FlexLayoutItem({
            layout: LayoutEnum.row,
            parentLayout: columnLayout,
            items: [],
            col: this.currentField.col,
            row: this.currentField.row
        });
        columnLayout.items.push(this.currentLayout);

        return true;
    }

    /**
     *
     */
    private isNextFieldRowspanEquelThenCurrentLayout(): boolean {
        if (this.nextField.rowspan !== this.currentLayout.rowspan) {
            return false;
        }

        this.currentField = this.nextField;

        return true;
    }

    /**
     *
     */
    private isNextFieldRowspanGreaterThenCurrentLayout(): boolean {
        if (this.currentLayout.rowspan >= this.nextField.rowspan) {
            return false;
        }

        const columnLayout = this.createColumnLayoutForCurrentLayout();

        const fieldName = this.fieldsMatrix[columnLayout.row + this.currentField.rowspan][columnLayout.col];
        this.currentField = this.getFieldData(fieldName, this.layoutConfig);

        this.currentLayout = new FlexLayoutItem({
            layout: LayoutEnum.row,
            parentLayout: columnLayout,
            col: this.currentField.col,
            row: this.currentField.row,
            items: []
        });
        columnLayout.items.push(this.currentLayout);

        return true;
    }

    /**
     *
     */
    private isNextFieldRowspanLessThenCurrentLayout() {
        this.currentField = this.nextField;

        const columnLayout = new FlexLayoutItem({
            layout: LayoutEnum.column,
            parentLayout: this.currentLayout,
            col: this.currentLayout.col,
            row: this.currentField.row,
            items: []
        });
        if (!this.currentLayout.parentLayout) {
            this.currentLayout.items.push(columnLayout);
        } else {
            this.currentLayout.parentLayout.items.push(columnLayout);
        }

        this.currentLayout = new FlexLayoutItem({
            layout: LayoutEnum.row,
            parentLayout: columnLayout,
            col: this.currentField.col,
            row: this.currentField.row,
            items: []
        });
        columnLayout.items.push(this.currentLayout);
    }

    /**
     * Creates column layout and puts current layout inside. If current layout doesn't
     * have parent layout we have also create additional row layout as parent for column
     * because root element must be a row.
     */
    private createColumnLayoutForCurrentLayout(): FlexLayoutItem {
        let findIndex;
        const columnLayout = new FlexLayoutItem({
            layout: LayoutEnum.column,
            col: this.currentLayout.col,
            row: this.currentField.row,
            colspan: this.currentLayout.colspan,
            rowspan: this.currentLayout.rowspan,
            items: [this.currentLayout]
        });

        if (!this.currentLayout.parentLayout) {
            const rootLayout = new FlexLayoutItem({
                layout: LayoutEnum.row,
                col: 0,
                row: 0,
                items: [columnLayout]
            });
            columnLayout.parentLayout = rootLayout;

            findIndex = this.layoutGrid.findIndex(layout => {
                return `${layout.col}.${layout.row}` === `${this.currentLayout.col}.${this.currentLayout.row}`;
            });
            this.layoutGrid.splice(findIndex, 1, rootLayout);

            return columnLayout;
        }

        findIndex = this.currentLayout.parentLayout.items.findIndex(layout => {
            return `${layout.col}.${layout.row}` === `${this.currentLayout.col}.${this.currentLayout.row}`;
        });
        this.currentLayout.parentLayout.items.splice(findIndex, 1, columnLayout);

        return columnLayout;
    }

    /**
     * @param className - className
     * @param width - width
     * @param minWidth - minWidth
     * @param maxWidth - maxWidth
     */
    private createCssClass(className, width: string = null, minWidth: string = null, maxWidth: string = null) {
        let styles = '';
        if (!this.styleElement) {
            this.styleElement = document.createElement('style');
            this.styleElement.type = 'text/css';

            document.getElementsByTagName('head')[0].appendChild(this.styleElement);
        }
        if (width) {
            styles = `width: ${width} !important;`;
        }
        if (minWidth) {
            styles = `${styles} min-width: ${minWidth} !important;`;
        }
        if (maxWidth) {
            styles = `${styles} max-width: ${maxWidth} !important;`;
        }

        this.styleElement.innerHTML = `${this.styleElement.innerHTML} .${className} {${styles}}`;
    }
}
